import React, { useEffect } from 'react';
import { Sparkles, Download, CheckCircle, ArrowRight, Building2, Clock, Loader2, Info, Globe2, Phone, Users } from 'lucide-react';
import { ResultsSummary, EMPTY_RESULTS_SUMMARY, Place } from '../../../types/results.types';
import { BalanceResponse } from '../../../types/balance.types';
import { AccessInfo } from '../../../api/results';
import { computeReadiness } from '../../../state/listState';
import { usePostHog } from 'posthog-js/react';

interface HeroBarProps {
  summary: ResultsSummary | null | undefined;
  accessInfo: AccessInfo | null;
  balance: BalanceResponse | null;
  searchId?: number;
  currentPlan?: string | null;
  unlockableCount: number;
  verifiableCount: number;
  autoFindStatus?: string | null;
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindChargeableCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
  autoFindBusinessTarget?: number;
  autoFindBusinessAttemptCount?: number;
  autoFindMaxPeoplePerBusiness?: number;
  autoFindMonthlyPoolLimit?: number;
  autoFindMonthlyPoolUsed?: number;
  isNoWebsiteSearch?: boolean;
  places?: Place[];
  onFindOwners: () => void;
  onVerifyEmails: () => void;
  onExport: (format: 'csv' | 'excel') => void;
  onUpgradeClick?: () => void;
  onResultsLimitUpgradeClick?: () => void;
  loading?: boolean;
}

const SkeletonBlock: React.FC<{ w?: string; h?: string }> = ({ w = 'w-20', h = 'h-4' }) => (
  <div className={`animate-pulse bg-[#F0EDE8] rounded ${w} ${h}`} />
);

const Pill: React.FC<{ green?: boolean; children: React.ReactNode }> = ({ green, children }) => (
  <div className={`flex items-center gap-1.5 text-xs font-medium ${green ? 'text-emerald-700' : 'text-[#9CA3AF]'}`}>
    {green && <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />}
    {children}
  </div>
);

const FlowArrow: React.FC = () => (
  <div className="hidden md:flex items-center flex-shrink-0 px-2 text-[#C9C4BC]">
    <ArrowRight className="h-5 w-5" />
  </div>
);

function getMonograms(places: Place[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const place of places) {
    if (place.people && place.people.length > 0) {
      for (const person of place.people) {
        const firstName = person.corrected_first_name || person.first_name || '';
        const lastName = person.corrected_last_name || person.last_name || '';
        const initials = firstName && lastName
          ? firstName[0] + lastName[0]
          : (firstName[0] || lastName[0] || '?');
        if (!seen.has(initials)) {
          seen.add(initials);
          result.push(initials.toUpperCase());
          if (result.length >= 4) return result;
        }
      }
    }
  }
  return result;
}

const MONOGRAM_COLORS = ['#8A3033', '#0F6E56', '#6B7280', '#D97706', '#2563EB'];

const HeroBar: React.FC<HeroBarProps> = ({
  summary: summaryProp,
  accessInfo,
  balance,
  searchId,
  currentPlan,
  unlockableCount,
  verifiableCount,
  autoFindStatus,
  autoFindMode,
  autoFindSuccessLimit = 0,
  autoFindSuccessCount = 0,
  autoFindChargeableCount = 0,
  autoFindAttemptCount = 0,
  autoFindNoEmailCount = 0,
  autoFindCatchAllCount = 0,
  autoFindGuardrailReason,
  autoFindBusinessTarget = 0,
  autoFindBusinessAttemptCount = 0,
  autoFindMaxPeoplePerBusiness = 0,
  autoFindMonthlyPoolLimit = 0,
  autoFindMonthlyPoolUsed = 0,
  isNoWebsiteSearch = false,
  places = [],
  onFindOwners,
  onExport,
  onUpgradeClick,
  onResultsLimitUpgradeClick,
  loading = false,
}) => {
  const posthog = usePostHog();
  const isLoading = loading || !summaryProp;
  const summary = summaryProp ?? EMPTY_RESULTS_SUMMARY;

  const { totalDecisionMakers } = computeReadiness(summary);
  const peopleCredits = balance?.dm_unlocks_remaining ?? null;
  const peopleCreditsExhausted = peopleCredits !== null && peopleCredits <= 0;
  const peopleCreditsAvailable = peopleCredits === null || peopleCredits > 0;
  const savedPlaces = accessInfo?.places_unlocked ?? accessInfo?.total_places_found ?? 0;
  const totalPlaces = accessInfo?.total_places_found ?? savedPlaces;
  const totalFoundPlaces = accessInfo?.total_links_found || totalPlaces;
  const showResultsCapBanner = Boolean(accessInfo?.results_capped);
  const planLabel = currentPlan ? currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1) : 'Your plan';
  const dmUnlocked = summary.dm_unlocked;
  const verifiedContacts = summary.ready_persons_count;
  const realDmEmailsFound = summary.ready_personal_count ?? 0;
  const searchEmailsConfirmed = summary.ready_company_count ?? 0;
  const normalizedPlan = (currentPlan || '').toLowerCase();
  const isAutoFind = autoFindMode === 'discover_auto' || autoFindMode === 'paid_auto';
  const isDiscoverAuto = normalizedPlan === 'discover' && autoFindMode === 'discover_auto';
  const dmEmailsFound = realDmEmailsFound;
  const allDMsUnlocked = unlockableCount === 0 && dmEmailsFound > 0;
  const autoFindIsActive = isAutoFind && (autoFindStatus === 'PENDING' || autoFindStatus === 'RUNNING');
  const autoFindFound = autoFindSuccessCount || 0;
  const autoFindLimitLabel = autoFindSuccessLimit > 0 ? autoFindSuccessLimit.toLocaleString() : '25';
  const autoFindBusinessAttemptLabel = autoFindBusinessAttemptCount.toLocaleString();
  const autoFindPoolPaused = autoFindGuardrailReason === 'auto_monthly_pool_limit';
  const autoFindFinishedWithoutMatches = isDiscoverAuto && !autoFindIsActive && autoFindAttemptCount > 0 && autoFindFound === 0 && unlockableCount === 0;
  const autoFindRemainingLabel = unlockableCount.toLocaleString();
  const canFindRemainingPeople = peopleCreditsAvailable && unlockableCount > 0;
  const showAutoFindCompleteSuccess = isDiscoverAuto && !autoFindIsActive && !canFindRemainingPeople && !peopleCreditsExhausted;

  // Hero state: pre-find | partial | complete
  const heroState: 'pre-find' | 'partial' | 'complete' =
    allDMsUnlocked && summary.has_any_data
      ? 'complete'
      : dmEmailsFound > 0
        ? 'partial'
        : 'pre-find';

  const monograms = getMonograms(places);
  const monogramOverflow = Math.max(0, totalDecisionMakers - monograms.length);

  useEffect(() => {
    if (isLoading) return;
    posthog?.capture('readiness_displayed', {
      search_id: searchId,
      hero_state: heroState,
      displayed_ready_count: verifiedContacts,
      total_guaranteed: summary.total_guaranteed,
      verifiable_count: verifiableCount,
      dm_unlocked: dmUnlocked,
      place_count: totalPlaces,
    });
  }, [summary.ready_persons_count, summary.dm_unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFindClick = () => {
    posthog?.capture('hero_find_owners_clicked', { search_id: searchId, count: unlockableCount });
    if (peopleCreditsExhausted) {
      onUpgradeClick?.();
    } else {
      onFindOwners();
    }
  };

  const handleExportClick = () => {
    posthog?.capture('hero_export_clicked', { search_id: searchId });
    onExport('csv');
  };

  const renderResultsCapBanner = () => {
    if (!showResultsCapBanner || isLoading) return null;

    const savedLabel = savedPlaces.toLocaleString();
    const foundLabel = totalFoundPlaces.toLocaleString();
    const capLabel = accessInfo?.visible_results_cap ?? savedPlaces;
    const boldText = `You're seeing all ${savedLabel} businesses saved for this search.`;
    const restText = totalFoundPlaces > savedPlaces
      ? ` WebLeads found ${foundLabel} businesses, but ${planLabel} saves up to ${capLabel.toLocaleString()} per search. Upgrade before your next search.`
      : ` Upgrade before your next search to save more businesses.`;

    return (
      <div className="flex flex-col gap-2 border-b border-[#EDE1E1] bg-[#FCF7F7] px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex min-w-0 items-start gap-2">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 fill-[#8A3033] text-[#8A3033]" />
          <p className="min-w-0 flex-1 text-[11.5px] leading-relaxed text-[#6B7280]">
            <span className="font-semibold text-[#374151]">{boldText}</span>
            {restText}
          </p>
        </div>
        <button
          type="button"
          onClick={onResultsLimitUpgradeClick}
          className="inline-flex w-fit items-center justify-center rounded-md bg-[#8A3033] px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-[#722629] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033]"
        >
          Upgrade for larger searches
        </button>
      </div>
    );
  };

  const renderNoWebsiteHero = () => (
    <div className="mb-4 overflow-hidden rounded-xl border border-[#EDE9E3] bg-white shadow-sm">
      {renderResultsCapBanner()}

      <div className="hidden md:grid md:grid-cols-[1.05fr_1.35fr_1.15fr]">
        <div className="px-7 py-6">
          <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-[#F0EDE8]">
            <Building2 className="h-5 w-5 text-[#8B8580]" />
          </div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#6B7280]">
            Businesses with no known website
          </p>
          {isLoading ? (
            <div className="space-y-2">
              <SkeletonBlock w="w-20" h="h-10" />
              <SkeletonBlock w="w-52" h="h-3" />
            </div>
          ) : (
            <>
              <div className="flex items-end gap-3">
                <p className="text-5xl font-extrabold leading-none text-[#2D3142] tabular-nums">
                  {savedPlaces.toLocaleString()}
                </p>
                <span className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-[#E7E2DC] bg-[#F6F3EF] px-2.5 py-1 text-[11px] font-semibold text-[#5F6368]">
                  <Globe2 className="h-3.5 w-3.5" />
                  No website
                </span>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-[#6B7280]">
                All {savedPlaces.toLocaleString()} saved businesses match your no-website search.
              </p>
            </>
          )}
        </div>

        <div className="border-l border-[#EDE9E3] px-7 py-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#6B7280]">Outreach details</p>
          <div className="flex items-start gap-2.5">
            <Phone className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#8A3033]" />
            <p className="text-sm leading-relaxed text-[#4B5563]">
              Use available phone numbers and addresses to reach out. Use ratings to prioritize the strongest prospects.
            </p>
          </div>
          {!isLoading && totalDecisionMakers > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#DDE8E3] bg-[#F5F9F7] px-3 py-1.5 text-xs font-semibold text-[#374151]">
              <Users className="h-3.5 w-3.5 text-[#0F6E56]" />
              <span>
                <strong className="text-[#0F6E56]">{totalDecisionMakers.toLocaleString()}</strong>{' '}
                decision maker{totalDecisionMakers === 1 ? '' : 's'} found in public records
              </span>
            </div>
          )}
        </div>

        <div className="border-l border-[#EDE9E3] px-7 py-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#C84040]">Recommended next step</p>
          <button
            type="button"
            onClick={handleExportClick}
            className="inline-flex items-center gap-2 rounded-lg bg-[#8A3033] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#722629] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033]"
          >
            <Download className="h-4 w-4" />
            Export outreach list
          </button>
          <p className="mt-2 text-xs leading-relaxed text-[#6B7280]">
            Export the list for calls or your outreach workflow.
          </p>
        </div>
      </div>

      <div className="p-4 md:hidden">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
          Businesses with no known website
        </p>
        <div className="mt-2 flex items-end gap-2.5">
          {isLoading ? (
            <SkeletonBlock w="w-16" h="h-9" />
          ) : (
            <p className="text-4xl font-extrabold leading-none text-[#2D3142] tabular-nums">
              {savedPlaces.toLocaleString()}
            </p>
          )}
          <span className="mb-0.5 inline-flex items-center gap-1 rounded-full border border-[#E7E2DC] bg-[#F6F3EF] px-2.5 py-1 text-[10.5px] font-semibold text-[#5F6368]">
            <Globe2 className="h-3 w-3" />
            No website
          </span>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-[#4B5563]">
          Use available phone numbers and addresses to reach out. Use ratings to prioritize prospects.
        </p>
        {!isLoading && totalDecisionMakers > 0 && (
          <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0F6E56]">
            <Users className="h-3.5 w-3.5" />
            {totalDecisionMakers.toLocaleString()} decision maker{totalDecisionMakers === 1 ? '' : 's'} found in public records
          </p>
        )}
        <button
          type="button"
          onClick={handleExportClick}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#8A3033] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#722629] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033]"
        >
          <Download className="h-4 w-4" />
          Export outreach list
        </button>
      </div>
    </div>
  );

  // ── Column 1: Businesses saved ─────────────────────────────────────────────
  const renderCol1 = () => (
    <div className="flex-1 min-w-[160px] px-6 py-6">
      <div className="w-9 h-9 rounded-lg bg-[#F0EDE8] flex items-center justify-center mb-3">
        <Building2 className="h-5 w-5 text-[#9CA3AF]" />
      </div>
      <p className="text-xs font-bold tracking-widest text-[#9CA3AF] uppercase mb-2">Businesses saved</p>
      {isLoading ? (
        <div className="space-y-1.5">
          <SkeletonBlock w="w-20" h="h-9" />
          <SkeletonBlock w="w-32" h="h-3" />
        </div>
      ) : (
        <>
          <p className="text-5xl font-extrabold tabular-nums text-[#2D3142] leading-none">
            {savedPlaces.toLocaleString()}
          </p>
          <p className="text-xs text-[#9CA3AF] mt-1">
            {totalFoundPlaces > savedPlaces
              ? `${totalFoundPlaces.toLocaleString()} found across Google Maps`
              : 'All found businesses are shown'}
          </p>
        </>
      )}
    </div>
  );

  // ── Column 2: Who runs them ────────────────────────────────────────────────
  const renderCol2 = () => (
    <div className="flex-1 min-w-[200px] px-6 py-6">
      <p className="text-xs font-bold tracking-widest text-[#C84040] uppercase mb-2">We found who runs them</p>
      {isLoading ? (
        <div className="space-y-1.5">
          <SkeletonBlock w="w-28" h="h-5" />
          <SkeletonBlock w="w-40" h="h-3" />
          <SkeletonBlock w="w-36" h="h-3" />
        </div>
      ) : (
        <>
          {monograms.length > 0 && (
            <div className="flex items-center mb-2">
              <div className="flex -space-x-1.5">
                {monograms.map((initials, i) => (
                  <div
                    key={initials + i}
                    className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
                    style={{ background: MONOGRAM_COLORS[i % MONOGRAM_COLORS.length] }}
                  >
                    {initials}
                  </div>
                ))}
                {monogramOverflow > 0 && (
                  <div className="w-8 h-8 rounded-full border-2 border-white bg-[#F0EDE8] flex items-center justify-center text-[11px] font-bold text-[#6B7280]">
                    +{monogramOverflow}
                  </div>
                )}
              </div>
              <span className="ml-2 text-base font-extrabold text-[#2D3142] tabular-nums">
                {totalDecisionMakers.toLocaleString()}
              </span>
              <span className="ml-1 text-xs font-semibold text-[#6B7280]">decision makers</span>
            </div>
          )}
          <p className="text-xs text-[#9CA3AF] mb-2">Real owners and decision makers, the people who reply.</p>
          <div className="space-y-1">
            {heroState === 'pre-find' && (
              <>
                <Pill green>{dmEmailsFound > 0 ? `${dmEmailsFound} decision maker emails found` : '0 decision maker emails found yet'}</Pill>
                {searchEmailsConfirmed > 0 && <Pill green>{searchEmailsConfirmed} verified search emails found</Pill>}
              </>
            )}
            {heroState === 'partial' && (
              <>
                <Pill green>{dmEmailsFound.toLocaleString()} decision maker emails found</Pill>
	                {isDiscoverAuto ? (
	                  <Pill>
	                    <Clock className="h-3 w-3 flex-shrink-0 text-[#9CA3AF]" />
	                    Auto-find budget: up to {autoFindLimitLabel} people this search
	                  </Pill>
	                ) : (
                  <Pill>
                    <Clock className="h-3 w-3 flex-shrink-0 text-[#9CA3AF]" />
                    {unlockableCount.toLocaleString()} decision makers still missing emails
                  </Pill>
                )}
              </>
            )}
            {heroState === 'complete' && (
              <>
                <Pill green>{dmEmailsFound.toLocaleString()} decision maker emails found</Pill>
                <Pill green>All decision maker emails unlocked</Pill>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ── Column 3: Next step / CTA ──────────────────────────────────────────────
  const renderCol3 = () => (
    <div className="flex-1 min-w-[200px] px-6 py-6">
      {isLoading ? (
        <div className="space-y-2">
          <SkeletonBlock w="w-24" h="h-3" />
          <SkeletonBlock w="w-36" h="h-9" />
          <SkeletonBlock w="w-48" h="h-3" />
        </div>
      ) : heroState === 'complete' ? (
        <>
          <p className="text-xs font-bold tracking-widest text-[#9CA3AF] uppercase mb-2">Next step</p>
          <button
            onClick={handleExportClick}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0F6E56] px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#0C5C47] transition-colors"
          >
            <Download className="h-4 w-4" />
            Export {verifiedContacts > 0 ? `${verifiedContacts.toLocaleString()} contacts` : 'contacts'}
          </button>
          <p className="text-xs text-[#9CA3AF] mt-1.5">Everything unlocked. Review export options.</p>
        </>
      ) : isDiscoverAuto ? (
        <>
          <p className={`text-xs font-bold tracking-widest uppercase mb-2 ${
            showAutoFindCompleteSuccess ? 'text-[#0F6E56]' : 'text-[#C84040]'
          }`}>
            {showAutoFindCompleteSuccess ? 'Auto-find complete' : 'Recommended next step'}
          </p>
          {showAutoFindCompleteSuccess ? (
            <>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#E1F5EE] px-3 py-1 text-[11px] font-semibold text-[#0F6E56]">
                <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                {searchEmailsConfirmed.toLocaleString()} search email{searchEmailsConfirmed === 1 ? '' : 's'} ready
              </div>
              <p className="text-xs text-[#9CA3AF] mt-1.5">
                Checked {autoFindAttemptCount.toLocaleString()} person{autoFindAttemptCount === 1 ? '' : 's'} · no decision-maker email confirmed
              </p>
            </>
          ) : (
            <button
              onClick={handleFindClick}
              disabled={autoFindIsActive}
              aria-busy={autoFindIsActive}
              className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors ${
                autoFindIsActive
                  ? 'cursor-not-allowed bg-[#B9A2A3]'
                  : 'bg-[#8A3033] hover:bg-[#722629]'
              }`}
            >
              {autoFindIsActive ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {autoFindIsActive
                ? 'Finding across businesses'
                : peopleCreditsExhausted
                  ? unlockableCount > 0 ? 'Upgrade to check remaining people' : 'Upgrade to search more people'
                  : `Find emails for ${unlockableCount.toLocaleString()} more people`}
            </button>
          )}
          {!showAutoFindCompleteSuccess && (
            <>
              <p className={`text-xs mt-1.5 font-medium ${autoFindFinishedWithoutMatches ? 'text-[#374151]' : 'text-[#C84040]'}`}>
                {autoFindIsActive
                  ? `${autoFindFound.toLocaleString()} email${autoFindFound === 1 ? '' : 's'} found so far`
                  : autoFindFinishedWithoutMatches
                    ? `Checked ${autoFindAttemptCount.toLocaleString()} people in this search. No confirmed emails found.`
                    : canFindRemainingPeople
                      ? `Checked ${autoFindAttemptCount.toLocaleString()} people · ${autoFindRemainingLabel} remaining`
                      : `Found ${autoFindFound.toLocaleString()} email${autoFindFound === 1 ? '' : 's'} in this search`}
              </p>
              <p className="text-xs text-[#9CA3AF] mt-0.5">
                {autoFindIsActive
                  ? `${autoFindAttemptCount.toLocaleString()} people checked${autoFindMaxPeoplePerBusiness > 0 ? ` · up to ${autoFindMaxPeoplePerBusiness} per business before sparse refill` : ''}`
                  : peopleCreditsExhausted
                    ? `${autoFindChargeableCount.toLocaleString()} of ${autoFindLimitLabel} included people used${autoFindBusinessAttemptCount > 0 ? ` · ${autoFindBusinessAttemptLabel} business${autoFindBusinessAttemptCount === 1 ? '' : 'es'} checked` : ''}`
                    : 'People credits remain available for rows with decision makers.'}
              </p>
            </>
          )}
        </>
      ) : (
        <>
          <p className="text-xs font-bold tracking-widest text-[#C84040] uppercase mb-2">Recommended next step</p>
          <button
            onClick={handleFindClick}
            className="inline-flex items-center gap-2 rounded-lg bg-[#8A3033] px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#722629] transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            {heroState === 'partial'
              ? `Find emails for ${unlockableCount.toLocaleString()} more people`
              : 'Find decision maker emails'}
          </button>
          <p className="text-xs text-[#C84040] mt-1.5 font-medium">
            +{heroState === 'partial' ? unlockableCount.toLocaleString() : totalDecisionMakers.toLocaleString()} remaining · returned verified
          </p>
	          <p className="text-xs text-[#9CA3AF] mt-0.5">
	            {autoFindPoolPaused
	              ? 'Auto find paused to preserve your monthly credits. Select businesses to keep going.'
	              : autoFindMonthlyPoolLimit > 0
	                ? `${autoFindMonthlyPoolUsed.toLocaleString()} of ${autoFindMonthlyPoolLimit.toLocaleString()} monthly auto find credits used.`
	                : 'You only use a lookup when we find an email. Looking is always free.'}
	          </p>
        </>
      )}
    </div>
  );

  // ── Column 4: Verified contacts ───────────────────────────────────────────
  const renderCol4 = () => (
    <div className="flex-1 min-w-[160px] px-6 py-6">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-bold tracking-widest text-[#0F6E56] uppercase">Ready to contact</p>
      </div>
      {isLoading ? (
        <div className="space-y-1.5">
          <SkeletonBlock w="w-16" h="h-9" />
          <SkeletonBlock w="w-40" h="h-3" />
          <SkeletonBlock w="w-36" h="h-3" />
        </div>
      ) : (
        <>
          <p className="text-5xl font-extrabold tabular-nums text-[#0F6E56] leading-none">
            {verifiedContacts.toLocaleString()}
          </p>
          <div className="mt-2 space-y-1">
            {dmEmailsFound > 0 && (
              <p className="text-xs text-[#374151]">
                <span className="font-semibold">{dmEmailsFound.toLocaleString()}</span> decision maker emails
              </p>
            )}
            {searchEmailsConfirmed > 0 && (
              <p className="text-xs text-[#374151]">
                <span className="font-semibold">{searchEmailsConfirmed.toLocaleString()}</span> search emails
              </p>
            )}
            {verifiedContacts === 0 && (
              <p className="text-xs text-[#9CA3AF]">verified emails, ready today, grows as you find more.</p>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (isNoWebsiteSearch) {
    return renderNoWebsiteHero();
  }

  return (
    <div className="bg-white border border-[#EDE9E3] rounded-xl shadow-sm mb-4 overflow-hidden">
      {renderResultsCapBanner()}

      {/* Desktop: four columns */}
      <div className="hidden md:flex items-center">
        {renderCol1()}
        <FlowArrow />
        {renderCol2()}
        <FlowArrow />
        {renderCol3()}
        <FlowArrow />
        {renderCol4()}
      </div>

      {/* Mobile: vertical stack */}
      <div className="md:hidden divide-y divide-[#EDE9E3]">
        {renderCol1()}
        {renderCol2()}
        {renderCol3()}
        {renderCol4()}
      </div>
    </div>
  );
};

export default HeroBar;
